package com.siriusxm.pia.components

import androidx.compose.runtime.*
import com.siriusxm.pia.SXMUI
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.coroutines.cancellation.CancellationException


class PageContents<T>(val items: List<T>, val nextPageToken: String?)

//const val MAX_VISIBLE_PAGES = 5

/**
 * Styles for the pagination control
 */
object PaginationStyles : StyleSheet() {
    val nav by style {
        cursor("pointer")
    }

    val navDisabled by style {
        color(SXMUI.disabledTextColor.value())
        cursor("default")
    }

    val pageNumber by style {
        margin(0.px, 5.px)
        lineHeight((1.7).cssRem)
        display(DisplayStyle.InlineBlock)
        height((1.7).cssRem)
        property("vertical-align", "top")
    }

    val selectedPage by style {
        fontWeight("bold")
    }
}

/**
 * Renders a Lazy pagination control with LastEvaluatedKey. Supports indefinite pagination when the total
 * number of items may not be known.
 */
@Composable
fun <T> LazyPaginationControl(
    key: Any?,
    maxPages: Int = Int.MAX_VALUE,
    maxVisiblePages: Int = 5,
    updater: (List<T>?) -> Unit,
    fetcher: suspend (token: String?) -> PageContents<T>,
    onError: (Throwable) -> Unit = {}
) {
    var pageCache by remember(key) { mutableStateOf(mapOf<Int, PageContents<T>>()) }
    var pageNumber by remember(key) { mutableStateOf(0) }
    var isLoading by remember(key) { mutableStateOf(false) }

    LaunchedEffect(pageNumber, key) {
        if (!isLoading && pageNumber < maxPages && pageCache[pageNumber] == null) {
            isLoading = true
            try {
                val token = pageCache[pageNumber - 1]?.nextPageToken
                val pageItems = fetcher(token)
                pageCache = pageCache + (pageNumber to pageItems)
            } catch (e: CancellationException) {
                // The effect was cancelled because the key changed or the control left the
                // composition. That is not a fetch failure, so it must not reach onError.
                throw e
            } catch (e: Throwable) {
                onError(e)
            } finally {
                isLoading = false
            }
        }
    }

    // Notify the caller when contents have changed.
    LaunchedEffect(pageCache, pageNumber) {
        val items = pageCache.get(pageNumber)?.items
        if (items != null) {
            updater(items)
        }
    }

    //data for current page being viewed
    val currentPageData = pageCache[pageNumber]
    // there are more pages to fetch
    val hasNext = currentPageData?.nextPageToken != null
    // the highest page number we have cached
    val maxLoadedPage = pageCache.keys.maxOrNull() ?: 0
    // true if at least one loaded page has results
    val anyPageHasItems = pageCache.values.any { it.items.isNotEmpty() }

    if (anyPageHasItems && (hasNext || pageCache.size > 1 || isLoading)) {
        Div({
            style {
                display(DisplayStyle.InlineBlock)
            }
        }) {
            Span({
                classes(PaginationStyles.nav)
                if (pageNumber == 0) classes(PaginationStyles.navDisabled)
                onClick {
                    if (pageNumber > 0) pageNumber--
                }
            }) { icon("chevron_left") }

            val totalPages = pageCache.size
            val lastPage = totalPages - 1
            val visibleRange = maxVisiblePages

            var lastGap = false

            repeat(totalPages) { i ->
                val isFirst = i == 0
                val isLast = i == lastPage
                val isInWindow = i in (pageNumber - visibleRange)..(pageNumber + visibleRange)

                if (isFirst || isLast || isInWindow) {
                    pageButton(i, pageNumber) { pageNumber = it }
                    lastGap = false
                }
                val isGap = !isFirst && !isLast && !isInWindow
                // only show one set of ellipses
                if (isGap && !lastGap) {
                    Span({ classes(PaginationStyles.pageNumber) }) { Text("…") }
                    lastGap = true
                }
            }
            if (isLoading) {
                spinner(size = Size.SMALL)
            }
            if (hasNext) {
                Span({ classes(PaginationStyles.pageNumber) }) { Text("…") }
            }
            if (hasNext || pageNumber < maxLoadedPage) {
                Span({
                    classes(PaginationStyles.nav)
                    onClick {
                        pageNumber++
                    }
                }) { icon("chevron_right") }
            }
        }
    }
}

@Composable
private fun pageButton(index: Int, current: Int, onClick: (Int) -> Unit) {
    Span({
        classes(PaginationStyles.nav, PaginationStyles.pageNumber)
        if (index == current) classes(PaginationStyles.selectedPage)
        onClick { onClick(index) }
    }) {
        Text((index + 1).toString())
    }
}